export const DESKTOP_MEDIA_QUERY = "(min-width: 768px)";

/** Radix SelectVendor popper sideOffset. */
export const SELECT_VENDOR_SIDE_OFFSET_PX = 12;

/** Pharmacy list separator height. */
export const SELECT_VENDOR_SEPARATOR_HEIGHT_PX = 1;

/**
 * SelectVendor footerSlot container (py-4) plus confirm button (p-4).
 * Rendered outside ContentWrapper on desktop courier/pickup flows.
 */
export const SELECT_VENDOR_FOOTER_SLOT_HEIGHT_PX = 88;

/** Extra space reserved for font metrics and subpixel rounding. */
export const SELECT_VENDOR_FOOTER_SAFETY_PX = 4;

const VIEWPORT_BOTTOM_PADDING_PX = 20;
const DEFAULT_MIN_LIST_HEIGHT_PX = 120;

export const DESKTOP_LIST_MAX_HEIGHT_PX = {
	default: 400,
} as const;

/**
 * Figma: the desktop pharmacy picker panel is 616px tall — the same height the
 * no-results state already uses. The scrollable list takes whatever is left of
 * it after the sticky header and the confirm footer, so the panel lands on 616
 * whether or not the filter row and the footer are there, instead of each
 * combination hard-coding its own list cap.
 *
 * This is only the ceiling. A short viewport still wins below, so the footer
 * cannot be pushed off screen.
 */
export const DESKTOP_PHARMACY_PANEL_HEIGHT_PX = 616;

function getFooterReservationHeight(
	params: Pick<GetSelectVendorListHeightParams, "isDesktop" | "hasFooterSlot">,
): number {
	if (!params.isDesktop || !params.hasFooterSlot) {
		return 0;
	}
	return SELECT_VENDOR_FOOTER_SLOT_HEIGHT_PX + SELECT_VENDOR_FOOTER_SAFETY_PX;
}

function getMinListHeight(params: GetSelectVendorListHeightParams): number {
	if (params.isDesktop && params.isPharmacy && params.hasFooterSlot) {
		return 0;
	}
	return DEFAULT_MIN_LIST_HEIGHT_PX;
}

/** Returns true when the viewport matches the desktop breakpoint. */
export function isDesktopViewport(): boolean {
	if (
		typeof window === "undefined" ||
		typeof window.matchMedia !== "function"
	) {
		return false;
	}
	return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
}

/**
 * The tallest the list may be on desktop: everything the 616px panel has left
 * over, or the generic popover cap for non-pharmacy selects.
 */
function getDesktopListMaxHeight(
	params: GetSelectVendorListHeightParams,
): number {
	if (!params.isPharmacy) {
		return DESKTOP_LIST_MAX_HEIGHT_PX.default;
	}
	// The footer's own height, not getFooterReservationHeight: that adds a few
	// pixels of slack for font metrics, which belongs in the viewport-fit
	// calculation but would leave the panel short of its target height here.
	const footerHeight = params.hasFooterSlot
		? SELECT_VENDOR_FOOTER_SLOT_HEIGHT_PX
		: 0;
	return Math.max(
		DEFAULT_MIN_LIST_HEIGHT_PX,
		DESKTOP_PHARMACY_PANEL_HEIGHT_PX -
			params.searchSectionHeightPx -
			SELECT_VENDOR_SEPARATOR_HEIGHT_PX -
			footerHeight,
	);
}

export type GetSelectVendorListHeightParams = {
	viewportHeight: number;
	triggerBottom: number;
	searchSectionHeightPx: number;
	hasFooterSlot: boolean;
	isPharmacy: boolean;
	isDropdownOpen: boolean;
	isDesktop: boolean;
	sideOffsetPx?: number;
};

/**
 * Computes the scrollable pharmacy list height so the popover footer stays in view.
 */
export function getSelectVendorListHeight(
	params: GetSelectVendorListHeightParams,
): number {
	const footerHeight = getFooterReservationHeight(params);
	const sideOffsetPx = params.sideOffsetPx ?? SELECT_VENDOR_SIDE_OFFSET_PX;
	const bottomPadding =
		params.isPharmacy && params.isDropdownOpen ? 0 : VIEWPORT_BOTTOM_PADDING_PX;
	const availableHeight =
		params.viewportHeight -
		params.triggerBottom -
		sideOffsetPx -
		params.searchSectionHeightPx -
		SELECT_VENDOR_SEPARATOR_HEIGHT_PX -
		footerHeight -
		bottomPadding;
	const boundedHeight = Math.max(getMinListHeight(params), availableHeight);
	if (!params.isDesktop) {
		return boundedHeight;
	}
	return Math.min(boundedHeight, getDesktopListMaxHeight(params));
}
